import { useNavigate } from "react-router-dom";
import { X, Feather, Flame, Users, ArrowRight } from "lucide-react";

const CATEGORIES = [
  {
    id: "life",
    name: "Life Story",
    description: "A comprehensive biography — childhood to now.",
    tagline: "Every chapter of your journey",
    icon: Feather,
    accent: "from-[#7A9B7E] to-[#4A5D4A]",
  },
  {
    id: "short",
    name: "Short Story / Incident",
    description: "One powerful moment explored in vivid detail.",
    tagline: "A single event, deeply told",
    icon: Flame,
    accent: "from-[#C67B5C] to-[#8A4A30]",
  },
  {
    id: "collaborative",
    name: "Collaborative Book",
    description: "Invite family & friends to add memories together.",
    tagline: "A group memoir, many voices",
    icon: Users,
    accent: "from-[#6B86A8] to-[#3D5A80]",
  },
];

const StoryCategoryPicker = ({ onSelect, onClose }) => {
  const navigate = useNavigate();

  const handlePick = (id) => {
    if (id === "collaborative") {
      onClose?.();
      navigate("/occasions");
      return;
    }
    onSelect?.(id);
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="story-category-picker">
      <div
        className="bg-[#F8F6F1] w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative p-6 border-b border-[#D4C9B8]/40">
          <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded-full bg-[#E5D4C1]/50 hover:bg-[#D4C9B8] transition" data-testid="close-category-picker">
            <X className="w-4 h-4 text-[#4A5D4A]" />
          </button>
          <p className="font-sans text-[10px] uppercase tracking-[0.2em] text-[#7A9B7E] mb-1">New memoir</p>
          <h2 className="font-serif text-2xl md:text-3xl text-[#2D3A2D]">What are we writing today?</h2>
          <p className="font-sans text-sm text-[#7A8A7A] mt-1">Pick a format — we'll tailor the interview style to match.</p>
        </div>

        <div className="p-5 space-y-3">
          {CATEGORIES.map((c) => {
            const Icon = c.icon;
            return (
              <button
                key={c.id}
                onClick={() => handlePick(c.id)}
                className="group w-full text-left rounded-2xl bg-white border border-[#D4C9B8]/50 hover:border-[#7A9B7E] hover:shadow-lg transition-all p-4 flex items-center gap-4"
                data-testid={`category-${c.id}`}
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${c.accent} flex items-center justify-center flex-shrink-0 shadow-md`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-serif text-lg text-[#2D3A2D] mb-0.5 group-hover:text-[#4A5D4A]">{c.name}</p>
                  <p className="font-sans text-xs text-[#7A8A7A]">{c.description}</p>
                  <p className="font-sans text-[10px] italic text-[#9FB89F] mt-1">— {c.tagline}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-[#7A8A7A] group-hover:text-[#4A5D4A] group-hover:translate-x-1 transition-all flex-shrink-0" />
              </button>
            );
          })}
        </div>

        <div className="px-5 pb-5">
          <p className="text-center text-[10px] font-sans text-[#9FB89F]">You can always switch between memoirs — nothing locks you in.</p>
        </div>
      </div>
    </div>
  );
};

export default StoryCategoryPicker;
